/**
 * Bitmap thumbnail generation for design materials.
 * @module
 */
import Flags from "common/constants/flags";
import type { MaterialSrc } from "common/materials/materialSrc";
import { materialCacheKey } from "common/materials/materialSrc";
import { encodePath } from "common/utils/encodePath";
import * as THREE from "three";
import { bakedMaterialsFileUrlHelper } from "./bakedMaterialsFileUrlHelperRegistry";
import {
  designMaterialBakeRepeatPeriodAndRotation,
  designMaterialBakesToFlatColor,
  designMaterialColorVariantsCanUseTemplateTexture,
  flatColorBakedMaterial,
} from "./materials/designMaterialBakeColorMapUtils";
import type { BakedColorTexture } from "./materials/designMaterialTypes";
import type { DesignMaterialParamsPreprocessed } from "./materials/designMaterialTypes";

export const currentMaterialBakingCacheId = "v0"; // As in version 0. Should be changed if the material thumbnail "look" (lighting etc.) significantly changes.
export const materialBakingCacheDevSuffix = "-dev"; // We use a separate cache on the local development server.

export const currentMaterialBakingCacheIdFull =
  currentMaterialBakingCacheId + (Flags.IS_DEV_MODE ? materialBakingCacheDevSuffix : "");

// Keep the most recently used material baked textures in a cache.
const bakedTextureFetchCacheMaxSize = 50;

interface BakedTextureCacheItem {
  readonly key: string;
  readonly texture: Promise<BakedColorTexture>;
  lastUseTime: number;
}

interface TextureCacheItem {
  readonly key: string;
  readonly texture: Promise<THREE.Texture | undefined>;
  lastUseTime: number;
}

const flatColorCache: { [key: string]: BakedTextureCacheItem } = {};
const materialTextureFetchCache: TextureCacheItem[] = [];

function findFromCache(key: string): TextureCacheItem | undefined {
  return materialTextureFetchCache.find((cacheItem) => cacheItem.key === key);
}

function addToBakedTextureFetchCache(texture: Promise<THREE.Texture | undefined>, key: string) {
  if (materialTextureFetchCache.length >= bakedTextureFetchCacheMaxSize) {
    materialTextureFetchCache.sort((a, b) => a.lastUseTime - b.lastUseTime);
    materialTextureFetchCache.splice(0, 1);
  }
  materialTextureFetchCache.push({
    key,
    texture,
    lastUseTime: Date.now(),
  });
}

export function getMaterialBakingAwsRoot(cacheId: string) {
  return encodePath(["material_baked_textures", cacheId]);
}

export function getMaterialBakingAwsPath(cacheId: string, materialSrc: MaterialSrc, isColoredTemplateTexture: boolean) {
  const commonComponents = ["material_baked_textures", cacheId];
  if (isColoredTemplateTexture) {
    return encodePath([...commonComponents, materialSrc.templateId + "_template.png"]);
  }
  return encodePath([...commonComponents, materialCacheKey(materialSrc) + ".png"]);
}

function flatColorTextureCached(
  params: DesignMaterialParamsPreprocessed,
  materialSrc: MaterialSrc,
): Promise<BakedColorTexture> {
  const key = materialCacheKey(materialSrc);
  if (flatColorCache.hasOwnProperty(key)) {
    flatColorCache[key].lastUseTime = Date.now();
    return flatColorCache[key].texture;
  }
  const texture = Promise.resolve(flatColorBakedMaterial(params));
  flatColorCache[key] = {
    key,
    texture,
    lastUseTime: Date.now(),
  };
  return texture;
}

export function designMaterialBakedColorTextureFetch(
  materialSrc: MaterialSrc,
  params: DesignMaterialParamsPreprocessed,
): Promise<BakedColorTexture> {
  if (designMaterialBakesToFlatColor(params)) {
    return flatColorTextureCached(params, materialSrc);
  }

  const isColoredTemplateTexture =
    materialSrc.layerConfigId === undefined && designMaterialColorVariantsCanUseTemplateTexture(params);

  const imgPath = getMaterialBakingAwsPath(currentMaterialBakingCacheIdFull, materialSrc, isColoredTemplateTexture);

  let texturePromise: Promise<THREE.Texture | undefined>;
  const cachedTexture = findFromCache(imgPath);
  if (cachedTexture) {
    cachedTexture.lastUseTime = Date.now();
    texturePromise = cachedTexture.texture;
  } else {
    texturePromise = (async () => {
      const fileUrlHelper = await bakedMaterialsFileUrlHelper;
      const imgURL = await fileUrlHelper.getPostedFileURLByName(imgPath);

      if (!imgURL) {
        return undefined;
      }

      const imgFromAWS = document.createElement("img");

      try {
        await new Promise((resolveLoad, rejectLoad) => {
          imgFromAWS.crossOrigin = "Anonymous";
          imgFromAWS.addEventListener("load", () => resolveLoad(imgFromAWS));
          imgFromAWS.addEventListener("error", (err) => rejectLoad(err));
          imgFromAWS.src = imgURL;
        });
      } catch (e) {
        return undefined;
      }

      const texture = new THREE.Texture(
        imgFromAWS,
        THREE.UVMapping,
        THREE.RepeatWrapping,
        THREE.RepeatWrapping,
        THREE.LinearFilter,
        THREE.LinearMipmapLinearFilter,
        THREE.RGBAFormat,
        THREE.UnsignedByteType,
        0,
        THREE.SRGBColorSpace,
      );
      texture.needsUpdate = true;

      return texture;
    })();

    addToBakedTextureFetchCache(texturePromise, imgPath);
  }

  return (async () => {
    const texture = await texturePromise;
    if (!texture) {
      return flatColorTextureCached(params, materialSrc);
    }

    const { repeatPeriodX, repeatPeriodY, rotation } = designMaterialBakeRepeatPeriodAndRotation(params);

    if (isColoredTemplateTexture) {
      return {
        texture,
        color: new THREE.Color(params.color),
        worldWidth: repeatPeriodX,
        worldHeight: repeatPeriodY,
        rotation,
        loadedPromise: Promise.resolve(),
      };
    }
    return {
      texture,
      worldWidth: repeatPeriodX,
      worldHeight: repeatPeriodY,
      rotation,
      loadedPromise: Promise.resolve(),
    };
  })();
}
